import React, { useEffect, useState } from "react";
import { useLoading } from "../Loading";
import { collection, doc, getDoc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { useAuth } from "../../contexts/authContext";
import db, { storage } from "../../firebase/firebaseConfig";
import { getDownloadURL, ref } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { format } from "date-fns";

function MyApplication() {
    const { setLoading } = useLoading();
    const { currentUser } = useAuth();
    const [applicationData, setApplicationData] = useState();
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState();

    useEffect(() => {
        if (!currentUser) {
            navigate('/');
            return;
        }

        const applicationRef = query(collection(db, 'Applications'), where('adopterId', '==', currentUser.uid));
        const unsubscribeMyApplications = onSnapshot(applicationRef, (applicationSnap) => {
            if (applicationSnap.empty) {
                setApplicationData([]);
                setLoading(false);
                return;
            }

            const applications = applicationSnap.docs
                .map((doc) => ({
                    petId: doc.data().petId,
                    status: doc.data().status,
                    applicationId: doc.id,
                    timestamp: doc.data().timestamp.toDate(),
                }))
                .sort((a, b) => b.timestamp - a.timestamp)
                .map((app) => ({
                    ...app,
                    timestamp: format(app.timestamp, 'M/d/yyyy, h:mm:ss a'),
                }));

            const unsubscribeMyPetsAndAppointments = [];

            const processApplications = async () => {
                const updatedApplications = await Promise.all(
                    applications.map(async (app) => {
                        if (!app.petId) {
                            return {};
                        }

                        return new Promise((resolve) => {
                            const petRef = doc(db, 'Pets', app.petId);
                            const unsubscribeMyPet = onSnapshot(petRef, (petSnap) => {
                                let updatedData = { ...app };
                                if (petSnap.exists()) {
                                    updatedData = { ...app, petName: petSnap.data().petName, image: petSnap.data().image };
                                }

                                const appointmentRef = doc(db, 'Appointments', app.applicationId);
                                const unsubscribeMyAppointments = onSnapshot(appointmentRef, (appointmentSnap) => {
                                    if (appointmentSnap.exists()) {
                                        updatedData = { ...updatedData, finalStatus: appointmentSnap.data().status };
                                    }

                                    if (updatedData.image) {
                                        const imageRef = ref(storage, `AdoptPets/${updatedData.image}`);
                                        getDownloadURL(imageRef)
                                            .then((url) => {
                                                updatedData = { ...updatedData, imageUrl: url };
                                                resolve(updatedData);
                                            })
                                            .catch((error) => {
                                                console.error(error);
                                                updatedData = { ...updatedData, imageUrl: null };
                                                resolve(updatedData);
                                            });
                                    } else {
                                        resolve(updatedData);
                                    }
                                }, (error) => {
                                    console.error("Error fetching appointment:", error);
                                    resolve({ ...updatedData, finalStatus: null });
                                });
                                unsubscribeMyPetsAndAppointments.push(unsubscribeMyAppointments);
                            }, (error) => {
                                console.error("Error fetching pet:", error);
                                resolve({ ...app, petName: null, image: null });
                            });
                            unsubscribeMyPetsAndAppointments.push(unsubscribeMyPet);
                        });
                    })
                );

                setApplicationData(updatedApplications);
                setLoading(false);
            };

            processApplications();

            return () => {
                unsubscribeMyApplications();
                unsubscribeMyPetsAndAppointments.forEach((unsubscribe) => unsubscribe());
            };
        }, (error) => {
            console.error("Error fetching applications:", error);
            setLoading(false);
        });

        return () => unsubscribeMyApplications();
    }, [currentUser, selectedApplication]);

    const status = (doc) => {
        if (doc.status !== "Approved") {
            switch (doc.status) {
                case "Pending":
                    return <p>Pending Review</p>;
                case "Rejected":
                    return <p>Application Rejected</p>;
                case "Cancel":
                    return <p>Application Cancelled</p>;
                default:
                    return <p>Loading</p>;
            }

        }
        else if (doc.status === "Approved" && !doc.finalStatus) {
            return <p>Application Accepted</p>
        }
        else if (doc.finalStatus) {
            switch (doc.finalStatus) {
                case "Cancel":
                    return <p>Visit Cancelled</p>;
                case "Pending":
                    return <p>Awaiting Visit</p>;
                case "Rejected":
                    return <p>Adoption Rejected</p>;
                case "Approved":
                    return <p>Adoption Successful</p>;
                default:
                    return <p>Loading</p>;
            }
        }
    }

    const handleClickOpen = async(applicationId) => {
        try {
            const appointmentRef = doc(db, 'Appointments', applicationId);
            const appointmentSnap = await getDoc(appointmentRef);
        
            if (appointmentSnap.exists()&&appointmentSnap.data().status!=="Cancel") {
              alert('You cannot cancel this application as an appointment has already been scheduled.');
              setSelectedApplication(applicationId);
            } else {
              setSelectedApplication(applicationId);
              setOpenDialog(true);
            }
          } catch (error) {
            console.error('Error fetching appointment data:', error);
            alert('An error occurred. Please try again.'); 
          }
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    const handleCancel = async () => {
        setOpenDialog(false);
        setLoading(true);
        if(selectedApplication){
            const applicationRef = doc(db, "Applications", selectedApplication)
            await updateDoc(applicationRef, {status: "Cancel"});
            window.location.reload();
            setLoading(false);
        }
       
    };

    return (
        <div className="applicationContainer">
            <h2>My Application</h2>

            <div className="row">
                {applicationData && (applicationData.map((doc) => (
                    < div className="applicationHistory" key={doc.applicationId}>
                        <div className="block" >
                            <img src={doc.imageUrl || "/blue.gif"} alt="application pet" />
                            <div>
                                <h3>{doc.petName}</h3>
                                {status(doc)}
                                <div style={{display:"flex", justifyContent:"space-between", flexWrap:"wrap"}}>
                                    <p>{doc.timestamp}</p>
                                    <div style={{ display: "flex", justifySelf: "right", gap: "15px", marginLeft: "10px"}}>
                                        <button disabled={doc.status === "Cancel" || doc.status === "Rejected" || (doc.finalStatus && doc.finalStatus !== "Cancel")}
                                            className="cancel" onClick={() => handleClickOpen(doc.applicationId)}>Cancel</button>
                                        <button className="viewApplication" onClick={() => navigate(`/application-status/${doc.applicationId}`)}>View</button>
                                    </div>
                            </div>
                            </div>
                        </div>
                        
                    </div>
                    

                )))}

                <Dialog style={{ zIndex: "9999" }}
                    open={openDialog}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">
                    <DialogTitle id="alert-dialog-title">
                        {"Do you want to cancel this pet application?"}
                    </DialogTitle>
                    <DialogActions>
                        <Button onClick={handleClose}>No</Button>
                        <Button onClick={handleCancel} autoFocus>
                            Yes </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div >
    );

}


export default MyApplication;